import {
  useContext,
  useMemo,
  useState,
  type KeyboardEvent,
  type Key,
  type ReactNode,
  type Ref,
} from 'react';
import {
  Button as AriaButton,
  ComboBox as AriaComboBox,
  ComboBoxStateContext,
  Group,
  Input,
  ListBox,
  ListBoxItem,
  Popover as AriaPopover,
} from 'react-aria-components';
import { CaretDownIcon, CheckIcon, XIcon } from '@phosphor-icons/react';
import { cn } from './lib/cn';
import { INPUT_RESET } from './lib/input-reset';
import { quietScrollbar } from './lib/scrollbar';
import type { PopoverPlacement } from './Popover';
import type { SelectOption } from './Select';
import { Spinner } from './Spinner';

/* Search-as-you-type relatives of `Select` and `MultiSelect`. Unlike
   `ComboBox`, neither keeps text it was not offered unless asked to: the
   single picks one of the options, the multi collects them as tags. */

/** A name for screen readers alone, or the id of a visible label. */
type SearchSelectName =
  | { 'aria-label': string; 'aria-labelledby'?: never }
  | { 'aria-labelledby': string; 'aria-label'?: never };

type SharedProps = SearchSelectName & {
  /** Typically the id of a validation message. */
  'aria-describedby'?: string;
  options: SelectOption[];
  onBlur?: () => void;
  onFocus?: () => void;
  /** Reports what is typed, e.g. to fetch options for it. */
  onInputChange?: (query: string) => void;
  /** Decides which options a query keeps. Defaults to a label substring match. */
  filter?: (option: SelectOption, query: string) => boolean;
  placeholder?: string;
  /** Shown when the query keeps no option. */
  emptyMessage?: string;
  /** Swaps the caret for a spinner while the options load. */
  isLoading?: boolean;
  isDisabled?: boolean;
  /** Draws a danger edge and is announced; the caller shows its own message. */
  isInvalid?: boolean;
  isRequired?: boolean;
  /** The `<input>` itself, e.g. for a form library to focus. */
  inputRef?: Ref<HTMLInputElement>;
  placement?: PopoverPlacement;
  className?: string;
  popoverClassName?: string;
};

export type SearchSelectProps = SharedProps & {
  /** An option's value; empty for none. */
  value?: string;
  onChange: (value: string) => void;
  /** Shows a button that empties the value, once there is one. */
  isClearable?: boolean;
};

export type TagSelectProps = SharedProps & {
  values: string[];
  onChange: (values: string[]) => void;
  /** Offers typed text the options lack as a tag of its own. */
  allowsCustomValue?: boolean;
  /** Runs, after `onChange`, when a typed tag is added. */
  onCreate?: (value: string) => void;
};

const CREATE_KEY = '__sk_create__';

function labelMatch(option: SelectOption, query: string) {
  return option.label.toLowerCase().includes(query.trim().toLowerCase());
}

const GROUP = [
  'sk:inline-flex sk:min-h-7 sk:w-full sk:items-center sk:gap-2 sk:px-3',
  'sk:inset-shadow-hairline sk:bg-surface sk:shadow-card',
  'sk:transition-[box-shadow] sk:duration-75 sk:focus-within:shadow-none',
].join(' ');

const INPUT = cn(
  INPUT_RESET,
  'sk:min-w-0 sk:flex-1 sk:bg-transparent sk:p-0 sk:text-dense sk:font-medium sk:tracking-dense sk:text-fg',
  // Legacy Semantic forms style every input inside `.ui.form` (0,2,1); the edge belongs to the group.
  'sk:rounded-none! sk:border-0! sk:bg-transparent! sk:p-0! sk:shadow-none!',
  'sk:outline-none sk:placeholder:font-normal sk:placeholder:text-fg-tertiary',
  // 16px stops iOS Safari zooming the page when the field takes focus.
  'sk:max-sm:text-base',
);

const POPOVER =
  'sk:z-3000 sk:w-(--trigger-width) sk:min-w-44 sk:rounded-card sk:inset-shadow-hairline sk:bg-surface sk:p-2 sk:shadow-card sk:outline-none';

const LIST = cn(
  'sk:flex sk:max-h-72 sk:flex-col sk:gap-1 sk:overflow-y-auto sk:outline-none',
  quietScrollbar,
);

const ITEM = [
  'sk:flex sk:shrink-0 sk:cursor-pointer sk:items-center sk:gap-2',
  'sk:rounded-option sk:px-3 sk:py-2 sk:text-dense sk:text-fg sk:outline-none',
  'sk:hover:bg-fill-quaternary sk:rac-focused:bg-fill-quaternary sk:rac-selected:bg-fill-tertiary',
].join(' ');

const ICON_BUTTON = [
  'sk:m-0 sk:appearance-none sk:border-0 sk:bg-transparent sk:p-0 sk:font-sans',
  'sk:inline-flex sk:shrink-0 sk:cursor-pointer sk:items-center sk:text-fg-secondary sk:hover:text-fg',
  'sk:outline-none sk:focus-visible:outline-2 sk:focus-visible:outline-solid sk:focus-visible:outline-offset-1 sk:focus-visible:outline-primary',
].join(' ');

function OptionContent({
  option,
  isSelected,
}: {
  option: SelectOption;
  isSelected?: boolean;
}) {
  return (
    <>
      {option.leading ? (
        <span className="sk:flex sk:shrink-0 sk:items-center">
          {option.leading}
        </span>
      ) : null}
      <span className="sk:flex sk:min-w-0 sk:flex-1 sk:flex-col sk:text-left">
        <span className="sk:truncate">{option.label}</span>
        {option.description ? (
          <span className="sk:truncate sk:text-xs sk:font-normal sk:text-fg-tertiary">
            {option.description}
          </span>
        ) : null}
      </span>
      {option.trailing ? (
        <span className="sk:ml-auto sk:shrink-0">{option.trailing}</span>
      ) : null}
      {isSelected ? (
        <CheckIcon size={12} weight="bold" className="sk:shrink-0" />
      ) : null}
    </>
  );
}

function Caret({ isLoading }: { isLoading?: boolean }) {
  return isLoading ? (
    <Spinner size="sm" className="sk:shrink-0 sk:text-fg-secondary" />
  ) : (
    <AriaButton className={ICON_BUTTON}>
      <CaretDownIcon size={10} weight="bold" />
    </AriaButton>
  );
}

function OptionList({
  items,
  emptyMessage,
  showCheck,
}: {
  items: SelectOption[];
  emptyMessage: string;
  showCheck?: boolean;
}) {
  return (
    <ListBox
      items={items}
      className={LIST}
      renderEmptyState={() => (
        <div className="sk:px-2 sk:py-3 sk:text-center sk:text-dense sk:text-fg-tertiary">
          {emptyMessage}
        </div>
      )}
    >
      {(option: SelectOption) => (
        <ListBoxItem
          id={option.value}
          textValue={option.label}
          /* Shown but not pickable, as in Select: the option says why in its description. */
          isDisabled={option.isDisabled}
          className={cn(
            ITEM,
            'sk:rac-disabled:cursor-not-allowed sk:rac-disabled:text-fg-tertiary sk:rac-disabled:hover:bg-transparent',
          )}
        >
          {({ isSelected }) => (
            <OptionContent
              option={option}
              isSelected={showCheck && isSelected}
            />
          )}
        </ListBoxItem>
      )}
    </ListBox>
  );
}

/** A select whose list narrows as you type. Typed text never becomes the value. */
export function SearchSelect({
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
  options,
  value,
  onChange,
  onBlur,
  onFocus,
  onInputChange,
  filter = labelMatch,
  placeholder = 'Select',
  emptyMessage = 'Nothing found',
  isClearable,
  isLoading,
  isDisabled,
  isInvalid,
  isRequired,
  inputRef,
  placement = 'bottom start',
  className,
  popoverClassName,
}: SearchSelectProps) {
  const selected = value
    ? options.find(option => option.value === value)
    : undefined;
  /* Null while nothing is typed, so the selected label shows through. */
  const [query, setQuery] = useState<string | null>(null);
  const inputValue = query ?? selected?.label ?? '';

  const shown = useMemo(
    () =>
      query === null || query === selected?.label
        ? options
        : options.filter(option => filter(option, query)),
    [options, query, selected?.label, filter],
  );

  return (
    <AriaComboBox
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      items={shown}
      selectedKey={selected ? selected.value : null}
      inputValue={inputValue}
      onInputChange={next => {
        setQuery(next);
        onInputChange?.(next);
      }}
      onSelectionChange={(key: Key | null) => {
        setQuery(null);
        if (key !== null && String(key) !== value) {
          onChange(String(key));
        }
      }}
      isDisabled={isDisabled}
      isInvalid={isInvalid}
      isRequired={isRequired}
      validationBehavior="aria"
      allowsEmptyCollection
      menuTrigger="focus"
      className="sk:inline-flex"
    >
      <Group
        className={cn(
          GROUP,
          'sk:h-7 sk:rounded-full',
          isInvalid && 'sk:inset-shadow-[0_0_0_1px_var(--sk-danger-fg)]',
          isDisabled && 'sk:cursor-not-allowed sk:opacity-50',
          className,
        )}
      >
        {selected?.leading && query === null ? (
          <span className="sk:flex sk:shrink-0 sk:items-center">
            {selected.leading}
          </span>
        ) : null}
        <Input
          ref={inputRef}
          placeholder={placeholder}
          onFocus={onFocus}
          onBlur={() => {
            setQuery(null);
            onBlur?.();
          }}
          className={INPUT}
        />
        {isClearable && selected && !isDisabled ? (
          <button
            type="button"
            aria-label="Clear"
            className={ICON_BUTTON}
            onClick={() => {
              setQuery(null);
              onChange('');
            }}
          >
            <XIcon size={12} weight="bold" />
          </button>
        ) : null}
        <Caret isLoading={isLoading} />
      </Group>

      <AriaPopover
        placement={placement}
        offset={6}
        className={cn(POPOVER, popoverClassName)}
      >
        <OptionList items={shown} emptyMessage={emptyMessage} showCheck />
      </AriaPopover>
    </AriaComboBox>
  );
}

/* Enter on typed text adds it as a tag, unless an option has keyboard focus —
   React Aria picks that one. Lives inside the combobox to read its state. */
function TagInput({
  inputRef,
  placeholder,
  onFocus,
  onBlur,
  onEnter,
  onBackspace,
}: {
  inputRef?: Ref<HTMLInputElement>;
  placeholder?: string;
  onFocus?: () => void;
  onBlur?: () => void;
  onEnter: () => boolean;
  onBackspace: () => void;
}) {
  const state = useContext(ComboBoxStateContext);

  return (
    <Input
      ref={inputRef}
      placeholder={placeholder}
      onFocus={onFocus}
      onBlur={onBlur}
      onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
        if (
          event.key === 'Enter' &&
          state?.selectionManager.focusedKey == null &&
          onEnter()
        ) {
          event.preventDefault();
        }
        if (event.key === 'Backspace' && !event.currentTarget.value) {
          onBackspace();
        }
      }}
      className={cn(INPUT, 'sk:min-w-16')}
    />
  );
}

function Tag({
  children,
  onRemove,
  label,
}: {
  children: ReactNode;
  onRemove?: () => void;
  label: string;
}) {
  return (
    <span className="sk:inline-flex sk:max-w-full sk:items-center sk:gap-1 sk:rounded-full sk:bg-fill-secondary sk:py-0.5 sk:pr-1.5 sk:pl-2 sk:text-xs sk:text-fg">
      <span className="sk:truncate">{children}</span>
      {onRemove ? (
        <button
          type="button"
          aria-label={`Remove ${label}`}
          className={ICON_BUTTON}
          onClick={onRemove}
        >
          <XIcon size={10} weight="bold" />
        </button>
      ) : null}
    </span>
  );
}

/**
 * Picks several options as removable tags, searching as you type. With
 * `allowsCustomValue`, typed text the options lack can be added too.
 */
export function TagSelect({
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
  options,
  values,
  onChange,
  onCreate,
  allowsCustomValue,
  onBlur,
  onFocus,
  onInputChange,
  filter = labelMatch,
  placeholder = 'Select',
  emptyMessage = 'Nothing found',
  isLoading,
  isDisabled,
  isInvalid,
  isRequired,
  inputRef,
  placement = 'bottom start',
  className,
  popoverClassName,
}: TagSelectProps) {
  const [query, setQuery] = useState('');
  const trimmed = query.trim();

  const shown = useMemo(() => {
    const left = options.filter(
      option =>
        !values.includes(option.value) && (!trimmed || filter(option, query)),
    );
    const known =
      values.includes(trimmed) ||
      options.some(
        option =>
          option.value === trimmed ||
          option.label.toLowerCase() === trimmed.toLowerCase(),
      );
    return allowsCustomValue && trimmed && !known
      ? [{ value: CREATE_KEY, label: `Add "${trimmed}"` }, ...left]
      : left;
  }, [options, values, query, trimmed, filter, allowsCustomValue]);

  const add = (next: string) => {
    setQuery('');
    onInputChange?.('');
    if (!values.includes(next)) {
      onChange([...values, next]);
    }
  };

  const create = () => {
    if (!allowsCustomValue || !trimmed || values.includes(trimmed)) {
      return false;
    }
    const match = options.find(
      option => option.label.toLowerCase() === trimmed.toLowerCase(),
    );
    if (match) {
      add(match.value);
      return true;
    }
    add(trimmed);
    onCreate?.(trimmed);
    return true;
  };

  const labelOf = (value: string) =>
    options.find(option => option.value === value)?.label ?? value;

  return (
    <AriaComboBox
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      items={shown}
      selectedKey={null}
      inputValue={query}
      onInputChange={next => {
        setQuery(next);
        onInputChange?.(next);
      }}
      onSelectionChange={(key: Key | null) => {
        if (key === null) {
          return;
        }
        if (key === CREATE_KEY) {
          create();
        } else {
          add(String(key));
        }
      }}
      isDisabled={isDisabled}
      isInvalid={isInvalid}
      isRequired={isRequired}
      validationBehavior="aria"
      allowsEmptyCollection
      menuTrigger="focus"
      className="sk:inline-flex"
    >
      <Group
        className={cn(
          GROUP,
          'sk:flex-wrap sk:rounded-card sk:py-1',
          isInvalid && 'sk:inset-shadow-[0_0_0_1px_var(--sk-danger-fg)]',
          isDisabled && 'sk:cursor-not-allowed sk:opacity-50',
          className,
        )}
      >
        {values.map(value => (
          <Tag
            key={value}
            label={labelOf(value)}
            onRemove={
              isDisabled
                ? undefined
                : () => onChange(values.filter(item => item !== value))
            }
          >
            {labelOf(value)}
          </Tag>
        ))}
        <TagInput
          inputRef={inputRef}
          placeholder={values.length ? undefined : placeholder}
          onFocus={onFocus}
          onBlur={() => {
            setQuery('');
            onBlur?.();
          }}
          onEnter={create}
          onBackspace={() => {
            if (values.length && !isDisabled) {
              onChange(values.slice(0, -1));
            }
          }}
        />
        <Caret isLoading={isLoading} />
      </Group>

      <AriaPopover
        placement={placement}
        offset={6}
        className={cn(POPOVER, popoverClassName)}
      >
        <OptionList items={shown} emptyMessage={emptyMessage} />
      </AriaPopover>
    </AriaComboBox>
  );
}
